/**
 * Manages a single AudioContext + AnalyserNode for live waveform visualization.
 *
 * - Reuses the same AudioContext across recordings (suspend/resume, never close).
 * - Provides peak sampling for waveform bars and per-frame amplitude for animation.
 * - Handles Safari's webkitAudioContext and visibility-change suspend quirk.
 */

export class WaveformAnalyzer {
  /** Shared AudioContext — suspended between recordings, never closed until destroy() */
  private audioCtx: AudioContext | null = null;

  /** AnalyserNode used to read frequency data from the mic stream */
  private analyser: AnalyserNode | null = null;

  /** The MediaStream source node feeding audio into the analyser */
  private source: MediaStreamAudioSourceNode | null = null;

  /** Reusable buffer for reading frequency data (avoids allocation per frame) */
  private dataArray: Uint8Array | null = null;

  /** Cached normalized average (0–1) to avoid redundant getByteFrequencyData calls */
  private cachedNormalized = 0;
  /** Timestamp of the last frequency data read (ms) */
  private lastReadTime = -1;

  /**
   * Connects the analyzer to a microphone stream.
   * Creates or resumes the AudioContext as needed.
   *
   * Uses `webkitAudioContext` as a fallback for older Safari versions.
   * The FFT size of 256 gives 128 frequency bins — enough resolution
   * for voice waveform visualization without excessive CPU usage.
   *
   * @param stream - The MediaStream from MicrophoneManager.acquire().
   */
  async connect(stream: MediaStream): Promise<void> {
    // Disconnect any existing source/analyser to prevent leaked graph nodes
    if (this.source) {
      this.source.disconnect();
      this.source = null;
    }
    this.analyser = null;
    this.dataArray = null;

    // Create or reuse AudioContext
    if (!this.audioCtx || this.audioCtx.state === "closed") {
      this.audioCtx = new (globalThis.AudioContext ||
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (globalThis as any).webkitAudioContext)();
    }

    if (this.audioCtx.state === "suspended") {
      await this.audioCtx.resume();
    }

    // Create analyser with settings optimized for voice waveform display
    const analyserNode = this.audioCtx.createAnalyser();
    analyserNode.fftSize = 256; // 128 frequency bins
    analyserNode.smoothingTimeConstant = 0.8; // Smooth out rapid fluctuations

    // Connect stream → analyser
    this.source = this.audioCtx.createMediaStreamSource(stream);
    this.source.connect(analyserNode);

    this.analyser = analyserNode;
    this.dataArray = new Uint8Array(analyserNode.frequencyBinCount);
  }

  /**
   * Reads frequency data from the analyser and caches the normalized average.
   * Deduplicates reads within the same millisecond (same rAF frame) so that
   * samplePeak() and sampleAmplitude() share a single getByteFrequencyData call.
   */
  private readNormalized(): number {
    if (!this.analyser || !this.dataArray) {
      return 0;
    }

    const now = Math.floor(performance.now());
    if (now !== this.lastReadTime) {
      this.analyser.getByteFrequencyData(this.dataArray);
      const avg =
        this.dataArray.reduce((a, b) => a + b, 0) / this.dataArray.length;
      this.cachedNormalized = avg / 255;
      this.lastReadTime = now;
    }

    return this.cachedNormalized;
  }

  /**
   * Returns a normalized 0–1 peak value for building waveform bars.
   * Called at {@link WAVEFORM_THROTTLE_MS} intervals.
   *
   * @returns A value between 0 and 1 representing the current audio intensity.
   *          Returns 0 if the analyzer is not connected.
   */
  samplePeak(): number {
    const normalized = this.readNormalized();
    // Multiply by 2 to boost sensitivity for voice (speech is lower intensity)
    return Math.max(0, Math.min(1, normalized * 2));
  }

  /**
   * Returns a live amplitude value suitable for driving the animated waveform UI.
   *
   * @returns A value between 5 and 50 representing the current amplitude.
   *          The minimum of 5 ensures the waveform is always slightly visible.
   *          Returns 0 if the analyzer is not connected.
   */
  sampleAmplitude(): number {
    const normalized = this.readNormalized();
    if (normalized === 0 && !this.analyser) {
      return 0;
    }
    return Math.round(normalized * 45) + 5; // +5 ensures minimum visible height
  }

  /**
   * Disconnects the source and suspends the AudioContext.
   * Does NOT close it — Safari has a limit on AudioContext instances.
   */
  disconnect(): void {
    if (this.source) {
      this.source.disconnect();
      this.source = null;
    }
    this.analyser = null;
    this.dataArray = null;

    if (this.audioCtx?.state === "running") {
      this.audioCtx.suspend();
    }
  }

  /** Resume the AudioContext (e.g. after visibility change on mobile). */
  async resume(): Promise<void> {
    if (this.audioCtx?.state === "suspended") {
      await this.audioCtx.resume();
    }
  }

  /** Whether the analyzer is currently connected to a stream. */
  get isConnected(): boolean {
    return this.analyser !== null;
  }

  /** Fully close the AudioContext. Call only on page unload / unmount. */
  destroy(): void {
    this.disconnect();
    if (this.audioCtx && this.audioCtx.state !== "closed") {
      this.audioCtx.close();
      this.audioCtx = null;
    }
  }
}
